import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Link, Unlink } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';

export const CurrencySelector: React.FC = () => {
  const { 
    currency, 
    currencyInfo, 
    changeCurrency, 
    supportedCurrencies, 
    isAutoLinked, 
    enableAutoLink, 
    disableAutoLink 
  } = useCurrency();
  const { t, i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside — only while open (INP)
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleCurrencyChange = (newCurrency: string) => {
    changeCurrency(newCurrency);
    setIsOpen(false);
  };

  const toggleAutoLink = () => {
    if (isAutoLinked) {
      disableAutoLink();
    } else {
      enableAutoLink();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        id="currency-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          isAutoLinked ? 'border-blue-400 dark:border-blue-500' : ''
        }`}
        data-testid="currency-selector"
        aria-label={t('navbar.selectCurrency')}
        title={isAutoLinked ? t('navbar.currencyLinked') : t('navbar.selectCurrency')}
      >
        <div className="flex items-center space-x-1">
          <Globe className="w-4 h-4 text-gray-500" />
          {isAutoLinked && (
            <Link className="w-3 h-3 text-blue-500" />
          )}
        </div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {(currencyInfo.symbol && currencyInfo.symbol !== currencyInfo.code) ? `${currencyInfo.symbol} ${currencyInfo.code}` : currencyInfo.code}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute top-full ${i18n.dir(i18n.language)==='rtl' ? 'left-0' : 'right-0'} mt-1 w-64 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto`}>
          {/* Option de liaison automatique */}
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={toggleAutoLink}
              className={`w-full flex items-center justify-center p-2 rounded transition-colors ${
                isAutoLinked 
                  ? 'bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50' 
                  : 'bg-yellow-200 hover:bg-yellow-300 dark:bg-yellow-600/30 dark:hover:bg-yellow-600/50'
              }`}
              title={isAutoLinked ? t('navbar.clickToUnlink') : t('navbar.clickToLink')}
            >
              {isAutoLinked ? (
                <Link className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <Unlink className="w-5 h-5 text-yellow-700 dark:text-yellow-300" />
              )}
            </button>
          </div>
          
          <div className="py-1">
            {Object.entries(supportedCurrencies).map(([code, info]) => (
              <button
                key={code}
                onClick={() => handleCurrencyChange(code)}
                className={`w-full ${i18n.dir(i18n.language)==='rtl' ? 'text-right' : 'text-left'} px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  currency === code 
                    ? 'bg-yellow-300 dark:bg-yellow-400 text-black' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`flex flex-col ${i18n.dir(i18n.language)==='rtl' ? 'items-end' : 'items-start'}`}>
                    <span className="font-medium">{info.name}</span>
                    <span className="text-xs text-gray-500">{info.code}</span>
                  </div>
                  <span className="text-base font-semibold">{info.symbol}</span>
                </div>
                {/* Surlignage jaune suffit pour l'état actif */}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};